import React, { useCallback, useMemo, useState } from "react";
import gql from "graphql-tag";
import Loading from "../../../components/Loading";
import Error from "../../../components/Error";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { ApolloClient } from "@apollo/client";
import {
  FormControl,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Theme,
  Typography,
} from "@material-ui/core";
import ConfirmationDialog from "../../../components";
import Live from "../../connx/dcx/Live";
import { useLiveDatas, useOnlineStatuses } from "../../connx/dcx/useLiveDatas";
import OnlineStatusFilter, {
  matchesStatusFilter,
  StatusFilter,
} from "../../connx/dcx/OnlineStatusFilter";
import { OnDCXDashEvent } from "../../connx/dcx/live/ActionMenu";
import { ConnXClient } from "../../../api/apolloClient";
import { REVOKE_OWNER, RevokeOwnerResult } from "../../../api/rvorderowner";
import { OrderProvider } from "../../../helpers/orderContext";

export interface Order {
  docID: string;
  name: string;
  desc: string;
  serialNumber: string;
  iotThingName: string;
  // the user group the order is assigned to
  userGroupID?: string;
  // hidden by a model/order admin, e.g. a device that no longer reports
  hidden?: boolean;
}

// heading for orders that are not in the user's group or one of its child groups
const OTHER_ORDERS = "Other orders";

export type OnReleaseOrderEvent = {
  id: string;
  name: string;
};

export interface DCXDashEvent {
  order: Order;
}

interface OrderListProps {
  userID: string;
  // the user group of the user, if they are in one
  groupID?: string;
  canViewDCX?: boolean;
  onDCXDash?: (e: DCXDashEvent) => void;
  connxClient?: ApolloClient<unknown>;
}

export const ORDER_IDS_QUERY = gql`
  query userRVOrderIDs($user: ID!) {
    userRVOrderIDs(user: $user)
  }
`;

interface OrderIdsQueryResult {
  userRVOrderIDs: string[];
}

export const ORDERS_QUERY = gql`
  query rvOrders($orderIDs: [ID!]!) {
    rvOrders(orderIDs: $orderIDs) {
      docID
      name
      desc
      serialNumber
      iotThingName
      userGroupID
      hidden
    }
  }
`;

interface QueryResult {
  rvOrders: Order[];
}

// all orders assigned to the user's group and, for a parent group, to its
// child groups
export const GROUP_ORDERS_QUERY = gql`
  query groupRVOrders {
    groupRVOrders {
      docID
      name
      desc
      serialNumber
      iotThingName
      userGroupID
      hidden
    }
  }
`;

interface GroupOrdersQueryResult {
  groupRVOrders: Order[];
}

// the user's group and its child groups, to name the group of each order
export const USER_GROUP_QUERY = gql`
  query UserGroup($id: ID!) {
    UserGroup(id: $id) {
      id
      name
      children {
        id
        name
      }
    }
  }
`;

interface UserGroupQueryResult {
  UserGroup?: {
    id: string;
    name: string;
    children: { id: string; name: string }[];
  };
}

const useStyles = makeStyles((theme: Theme) => ({
  orderGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: theme.spacing(2),
  },
  // spans the whole grid, above the orders of a group
  groupHeading: {
    gridColumn: "1 / -1",
    marginTop: theme.spacing(1),
  },
  groupFilter: {
    minWidth: 240,
    marginBottom: theme.spacing(2),
  },
  // first on the page, it is pulled into the page's top padding
  statusFilterFirst: {
    marginTop: theme.spacing(-2),
  },
}));

// the owner giving up their order; a user_admin does the same from Models and
// orders, with Release
export const RELEASE_ORDER_MUTATION = REVOKE_OWNER;

type mutationResult = RevokeOwnerResult;

const OrdersList: React.FC<OrderListProps> = ({
  userID,
  groupID,
  canViewDCX = false,
  onDCXDash,
  connxClient = ConnXClient,
}) => {
  const classes = useStyles();

  // list the orders owned by the user
  const {
    loading: queryLoading,
    error: queryError,
    data,
    refetch: refetchOrderIDs,
  } = useQuery<OrderIdsQueryResult>(ORDER_IDS_QUERY, {
    variables: { user: userID },
  });

  // query order details
  const {
    loading: queryDetailsLoading,
    error: queryDetailsError,
    data: detailsData,
  } = useQuery<QueryResult>(ORDERS_QUERY, {
    variables: { orderIDs: data?.userRVOrderIDs },
    skip: !data,
    client: connxClient,
  });

  // query the orders of the user's group
  const {
    loading: queryGroupLoading,
    error: queryGroupError,
    data: groupData,
  } = useQuery<GroupOrdersQueryResult>(GROUP_ORDERS_QUERY, {
    client: connxClient,
  });

  // The names of the user's group and its child groups. The orders are shown
  // without them if they cannot be loaded.
  const { loading: queryUserGroupLoading, data: userGroupData } = useQuery<
    UserGroupQueryResult
  >(USER_GROUP_QUERY, {
    variables: { id: groupID },
    skip: !groupID,
  });

  // order removal mutation
  const [
    releaseOrder,
    { loading: mutationLoading, error: mutationError },
  ] = useMutation<mutationResult>(RELEASE_ORDER_MUTATION);

  // state
  const [confirmReleaseID, setConfirmReleaseID] = useState<
    OnReleaseOrderEvent | undefined
  >(undefined);

  // release order callback handler
  const handleRelease = useCallback(
    async (e: OnReleaseOrderEvent) => {
      setConfirmReleaseID(e);
    },
    [setConfirmReleaseID]
  );

  // the user's own orders, plus the orders of their group; an order hidden by
  // a model/order admin is left out, owned or not
  const ownedIDs = useMemo(() => new Set(data?.userRVOrderIDs), [data]);
  const allOrders = useMemo(() => {
    const byID = new Map<string, Order>();
    // as per the models and orders dashboard, only group orders with a thing
    groupData?.groupRVOrders
      .filter((o) => "" !== o.iotThingName && !o.hidden)
      .forEach((o) => byID.set(o.docID, o));
    detailsData?.rvOrders
      .filter((o) => !o.hidden)
      .forEach((o) => byID.set(o.docID, o));
    return Array.from(byID.values());
  }, [groupData, detailsData]);

  // The live data of every order, read in one call for the whole page once
  // its orders are all known (the lists load in stages), so that filtering by
  // group neither waits nor asks again.
  const ordersKnown =
    !queryLoading && !queryDetailsLoading && !queryGroupLoading;
  const allOrderIDs = useMemo(
    () => (ordersKnown ? allOrders.map((o) => o.docID) : []),
    [ordersKnown, allOrders]
  );
  const liveDatas = useLiveDatas(allOrderIDs, connxClient);
  const liveData = liveDatas.byOrderID;

  // the groups the orders are shown under: the user's group first, then its
  // child groups by name
  const groups = useMemo(() => {
    const g = userGroupData?.UserGroup;
    if (!g) return [];
    const children = [...(g.children || [])].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    return [{ id: g.id, name: g.name }, ...children];
  }, [userGroupData]);

  // the position of the group an order is shown under; orders of no known
  // group come last
  const groupIndexOf = useCallback(
    (o: Order): number => {
      const i = groups.findIndex((g) => g.id === o.userGroupID);
      return i < 0 ? groups.length : i;
    },
    [groups]
  );
  const groupNameOf = useCallback(
    (o: Order): string => groups[groupIndexOf(o)]?.name || OTHER_ORDERS,
    [groups, groupIndexOf]
  );

  // orders are only grouped when they span more than one group
  const groupsWithOrders = useMemo(
    () => groups.filter((g) => allOrders.some((o) => o.userGroupID === g.id)),
    [groups, allOrders]
  );
  const grouped = useMemo(() => new Set(allOrders.map(groupIndexOf)).size > 1, [
    allOrders,
    groupIndexOf,
  ]);

  // group filter; all groups when empty
  const [groupFilter, setGroupFilter] = useState("");
  const handleGroupFilter = useCallback(
    (e: React.ChangeEvent<{ value: unknown }>) => {
      setGroupFilter(e.target.value as string);
    },
    []
  );
  // a filter for a group that no longer has any orders shows everything
  const activeFilter =
    grouped && groupsWithOrders.some((g) => g.id === groupFilter)
      ? groupFilter
      : "";

  // the orders of the group filter
  const groupOrders = useMemo(
    () =>
      allOrders
        .filter((o) => "" === activeFilter || o.userGroupID === activeFilter)
        .sort(
          (a, b) =>
            (grouped ? groupIndexOf(a) - groupIndexOf(b) : 0) ||
            a.name.localeCompare(b.name)
        ),
    [allOrders, activeFilter, grouped, groupIndexOf]
  );

  // how many of the orders of the group filter are online and offline
  const groupOrderIDs = useMemo(() => groupOrders.map((o) => o.docID), [
    groupOrders,
  ]);
  const { statusOf, online, offline } = useOnlineStatuses(
    groupOrderIDs,
    liveDatas
  );

  // all orders, or only those online or offline
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const orders = useMemo(
    () =>
      groupOrders.filter((o) =>
        matchesStatusFilter(statusFilter, statusOf(o.docID))
      ),
    [groupOrders, statusFilter, statusOf]
  );

  const handleDCXDash = useCallback(
    (e: OnDCXDashEvent) => {
      const order = orders.find((v) => v.docID === e.orderID);
      if (!order) return;
      onDCXDash?.({ order });
    },
    [onDCXDash, orders]
  );

  // rendering
  if (
    queryLoading ||
    mutationLoading ||
    queryDetailsLoading ||
    queryGroupLoading ||
    queryUserGroupLoading
  ) {
    return (
      <div data-testid="order-list-loading">
        <Loading />
      </div>
    );
  }
  if (queryError || mutationError || queryDetailsError || queryGroupError) {
    return (
      <div data-testid="order-list-error">
        <Error />
      </div>
    );
  }
  if (allOrders.length === 0) {
    const noOrders = "There are no orders for you or your group.";
    return <div data-testid="order-list-empty">{noOrders}</div>;
  }
  return (
    <>
      <ConfirmationDialog
        caption={`Confirm revocation of ownership of the order '${confirmReleaseID?.name}'.`}
        message="Are you sure you want to do this?"
        show={!!confirmReleaseID}
        onClose={async ({ cancelled }): Promise<void> => {
          if (!cancelled) {
            await releaseOrder({
              variables: { userID, orderID: confirmReleaseID?.id },
            });
            // the user no longer owns the order
            await refetchOrderIDs();
          }
          setConfirmReleaseID(undefined);
        }}
      />
      {grouped && (
        <FormControl className={classes.groupFilter}>
          <InputLabel id="order-group-filter-label" shrink>
            Group
          </InputLabel>
          <Select
            labelId="order-group-filter-label"
            value={activeFilter}
            onChange={handleGroupFilter}
            displayEmpty
            inputProps={{ "data-testid": "order-group-filter" }}
          >
            <MenuItem value="">All groups</MenuItem>
            {groupsWithOrders.map((g) => (
              <MenuItem key={g.id} value={g.id}>
                {g.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <OnlineStatusFilter
        className={grouped ? undefined : classes.statusFilterFirst}
        value={statusFilter}
        onChange={setStatusFilter}
        online={online}
        offline={offline}
      />
      {0 === orders.length && (
        <div data-testid="order-list-no-status-match">
          No {statusFilter} orders.
        </div>
      )}
      <div className={classes.orderGrid} data-testid="order-list">
        {orders.map((o, i) => {
          // only the owner of an order can override the generator or remove it
          const isOwner = ownedIDs.has(o.docID);
          // the orders of each group are shown under its name
          const groupName = groupNameOf(o);
          const showHeading =
            grouped && (0 === i || groupName !== groupNameOf(orders[i - 1]));
          return (
            canViewDCX && (
              <React.Fragment key={o.docID}>
                {showHeading && (
                  <Typography
                    variant="h6"
                    className={classes.groupHeading}
                    data-testid="order-group-heading"
                  >
                    {groupName}
                  </Typography>
                )}
                {/* FIXME: https://reactjs.org/docs/context.html#caveats */}
                <OrderProvider
                  docID={o.docID}
                  isOwner={isOwner}
                  hasThing={"" !== o.iotThingName}
                >
                  <Live
                    title={o.name}
                    description={o.desc}
                    serialNumber={o.serialNumber}
                    data={liveData.get(o.docID)}
                    status={statusOf(o.docID)}
                    onDCXDash={canViewDCX ? handleDCXDash : undefined}
                    onRelease={
                      isOwner
                        ? (): void => {
                            handleRelease({ id: o.docID, name: o.name });
                          }
                        : undefined
                    }
                  />
                </OrderProvider>
              </React.Fragment>
            )
          );
        })}
      </div>
    </>
  );
};

export default OrdersList;
